import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import api from "@/lib/api";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1666555038185-8323c553de64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwc3RlZWwlMjBtZXRhbCUyMHRleHR1cmUlMjBkYXJrfGVufDB8fHx8MTc4NTgzMzEyNHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, verifyCode } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(true);
  const [resent, setResent] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const r = await login(email, password, remember);
      if (r?.requires_code) { setChallenge(r); setCode(""); return; }
      nav("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { await verifyCode(challenge.challenge_id, code, trust); nav("/"); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const resend = async () => {
    setError(""); setResent("");
    try { await api.post("/auth/resend-code", { challenge_id: challenge.challenge_id }); setResent("Ny kode sendt."); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
  };

  if (challenge) return (
    <AuthShell caption="Bekræft login" title="Indtast koden fra din e-mail">
      <form onSubmit={submitCode} className="space-y-5" data-testid="code-step">
        <p className="text-sm text-stone-500 -mt-5">Vi har sendt en 6-cifret kode til <strong>{challenge.email_hint}</strong>. Koden gælder i 10 minutter.</p>
        <Input data-testid="login-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" autoFocus required className="h-14 rounded-xl bg-white text-center text-2xl tracking-[0.5em] font-semibold" />
        <div className="flex items-center gap-2">
          <Checkbox id="trust" data-testid="login-trust-device" checked={trust} onCheckedChange={(v) => setTrust(Boolean(v))} />
          <Label htmlFor="trust" className="text-sm font-medium text-stone-600 cursor-pointer select-none">Husk denne enhed i 30 dage</Label>
        </div>
        {error && <p data-testid="login-error" className="text-sm text-destructive font-medium">{error}</p>}
        {resent && <p data-testid="login-resent" className="text-sm text-[#2E7D32] font-medium">{resent}</p>}
        <Button data-testid="login-code-submit" type="submit" disabled={loading || code.length !== 6} className="w-full rounded-xl h-11 font-semibold text-[15px]">{loading ? "Kontrollerer…" : "Bekræft"}</Button>
        <div className="flex items-center justify-between text-sm">
          <button type="button" onClick={resend} className="text-[#A83232] hover:underline font-medium" data-testid="login-resend-code">Send ny kode</button>
          <button type="button" onClick={() => { setChallenge(null); setError(""); }} className="text-stone-500 hover:text-stone-900" data-testid="login-code-back">Tilbage</button>
        </div>
        <p className="text-xs text-stone-400">BSM beder dig aldrig om koden pr. telefon eller e-mail.</p>
      </form>
    </AuthShell>
  );

  return (
    <div className="min-h-screen grid lg:grid-cols-[55%_45%] bg-[#FBFAF8]">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={BG} alt="Stål" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0E0E0E]/90 via-[#141414]/80 to-[#141414]/85" />
        <div className="relative z-10 h-full flex flex-col justify-between p-14 text-white">
          <img src="/bsm-logo-white.png" alt="BSM A/S — vision in steel" className="h-11 w-auto self-start" />
          <div>
            <h1 className="text-5xl font-light tracking-tight leading-[1.05]">Projektportal</h1>
            <p className="mt-5 text-white/60 max-w-md text-base leading-relaxed">Få adgang til projekter, dokumenter, fremdrift og samarbejde – samlet ét sted.</p>
          </div>
          <p className="text-white/40 text-sm">© {new Date().getFullYear()} BSM A/S</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm rise-in">
          <img src="/bsm-logo.png" alt="BSM A/S — vision in steel" className="h-9 w-auto mb-10 lg:hidden" />
          <p className="label-caps">Intern adgang</p>
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight mt-2 mb-9 text-[#1A1919]">Log ind på portalen</h2>
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-stone-700">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="navn@bsm.dk" required className="h-11 rounded-xl bg-white" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-stone-700">Adgangskode</Label>
              <div className="relative">
                <Input id="password" data-testid="login-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="h-11 rounded-xl bg-white pr-11" />
                <button type="button" data-testid="toggle-password" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Skjul adgangskode" : "Vis adgangskode"} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700 transition-colors">
                  {showPw ? <EyeOff className="h-4.5 w-4.5" size={18} /> : <Eye className="h-4.5 w-4.5" size={18} />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 pt-1">
              <div className="flex items-center gap-2">
                <Checkbox id="remember" data-testid="login-remember" checked={remember} onCheckedChange={(v) => setRemember(Boolean(v))} />
                <Label htmlFor="remember" className="text-sm font-medium text-stone-600 cursor-pointer select-none">Husk mig</Label>
              </div>
              <Link to="/glemt-adgangskode" className="text-sm text-[#A83232] hover:underline font-medium" data-testid="login-forgot-link">Glemt adgangskode?</Link>
            </div>
            {error && <p data-testid="login-error" className="text-sm text-destructive font-medium">{error}</p>}
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full rounded-xl h-11 font-semibold text-[15px]">
              {loading ? "Logger ind…" : "Log ind"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
